import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLexicon } from '../../../hooks/useLexicon';
import { useParams, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import Loader from '../../Loader';
import Toast from '../../Toast';
import useShortAnswer from '../../../hooks/useShortAnswer';
import ItemSettingsModal from '../../Shared/ItemSettingsModal';
import SettingsTagsDisplay from '../../Shared/SettingsTagsDisplay';
import { useProfile } from '../../state/hooks';
import { useUserStates } from '../../../hooks/useUserStates';
import useSettingsFilters from '../../../hooks/useSettingsFilters';

import ImprovedSecondarySideBar from '../../ImprovedSecondarySideBar';
import MenuButton from '../../MenuButton';
import ScrollableArea from '../../Shared/ScrollableArea';
import { isTabletView } from '../../ReactiveRender';

import editIcon from '../../../static/img/markEdit.svg';
import EditHover from '../../../static/img/EditHover.svg';
import plusIcon from '../../../static/img/Plus-violet.svg';
import deleteIcon from '../../../static/icons/delete-logo.svg';

const MainContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100vh;
`;

const InnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const RightContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  background-color: #ffffff;
  min-height: 0;
  overflow: hidden;
`;

const ContentContainer = styled(ScrollableArea).attrs({ autoHide: 'leave' })`
  flex: 1;
  padding: 24px 0;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  min-height: 0;
`;

const TitleSection = styled.div`
  padding: 0 24px;
`;

const MainContentSection = styled.div`
  padding: 0 24px;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  margin-right: 8px;
  cursor: ${props => props.canEdit ? 'pointer' : 'default'};
  min-width: 0;
  flex: 1;

  &:hover {
    opacity: ${props => props.canEdit ? '0.8' : '1'};
  }
`;

const Title = styled.h1`
  font-family: IBM Plex Sans;
  font-size: 24px;
  font-weight: 600;
  margin: 0;
  color: #1e252a;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TitleInput = styled.input`
  font-family: IBM Plex Sans;
  font-size: 24px;
  font-weight: 600;
  outline: none;
  background: transparent;
  margin: 0;
  border: none;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #1e252a;
`;

const EditIconStyled = styled.img`
  width: 20px;
  height: 20px;
  opacity: 0.85;
  flex-shrink: 0;
  display: block;

  ${TitleContainer}:hover & {
    display: none;
  }
`;

const EditIconHover = styled.img`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  display: none;

  ${TitleContainer}:hover & {
    display: block;
  }
`;

const EditIconContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const HeadingAndFilterContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid #C9C6CC80;
`;

const HighestMarksContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 32px;
`;

const HighestMarksLabel = styled.span`
  font-family: IBM Plex Sans;
  font-size: 16px;
  font-weight: 500;
  color: #1e252a;
`;

const HighestMarksValue = styled.span`
  min-width: 60px;
  height: 40px;
  padding: 8px 12px;
  border: 1px solid #C9C6CC;
  border-radius: 6px;
  font-family: IBM Plex Sans;
  font-size: 16px;
  text-align: center;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: #F8F7F9;
  color: #1e252a;
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 24px;
`;

const ToggleLabel = styled.span`
  font-family: IBM Plex Sans;
  font-size: 14px;
  font-weight: 500;
  color: #4B464F;
`;

const ToggleSwitch = styled.button`
  position: relative;
  width: 40px;
  height: 22px;
  border-radius: 11px;
  border: none;
  cursor: pointer;
  background-color: ${props => props.$active ? '#7200E0' : '#C9C6CC'};
  transition: background-color 0.2s;
  padding: 0;
  flex-shrink: 0;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ToggleKnob = styled.div`
  position: absolute;
  top: 2px;
  left: ${props => props.$active ? '20px' : '2px'};
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background-color: #ffffff;
  transition: left 0.2s;
`;

const CriteriaHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 12px;
  padding: 0 8px;
`;

const HeaderLabel = styled.span`
  font-family: IBM Plex Sans;
  font-size: 14px;
  font-weight: 600;
  color: #4B464F;
`;

const MarksHeaderLabel = styled(HeaderLabel)`
  width: 60px;
`;

const CriteriaHeaderLabel = styled(HeaderLabel)`
  flex: 1;
`;

const CriteriaList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
`;

const CriteriaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px;
  border-radius: 6px;
  transition: background-color 0.2s;

  &:hover {
    background-color: #F8F7F9;
  }
`;

const MarksInputGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const MarksInput = styled.input`
  width: 60px;
  height: 44px;
  padding: 8px 12px;
  border: 1px solid ${props => props.$hasError ? '#DC2626' : '#C9C6CC'};
  border-radius: 6px;
  font-family: IBM Plex Sans;
  font-size: 16px;
  text-align: center;
  outline: none;
  flex-shrink: 0;

  &:focus {
    border-color: ${props => props.$hasError ? '#DC2626' : '#7200E0'};
    box-shadow: 0 0 0 2px ${props => props.$hasError ? 'rgba(220, 38, 38, 0.1)' : 'rgba(114, 0, 224, 0.1)'};
  }
`;

const MarksSeparator = styled.span`
  font-family: IBM Plex Sans;
  font-size: 16px;
  color: #4B464F;
  user-select: none;
`;

const RangeExpandButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1px solid #C9C6CC;
  background-color: transparent;
  cursor: pointer;
  flex-shrink: 0;
  font-size: 16px;
  color: #7200E0;
  transition: all 0.2s;
  padding: 0;

  &:hover {
    background-color: #F5EDFC;
    border-color: #7200E0;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const CriteriaInput = styled.input`
  flex: 1;
  height: 44px;
  padding: 8px 16px;
  border: 1px solid ${props => props.$hasError ? '#DC2626' : '#C9C6CC'};
  border-radius: 6px;
  font-family: IBM Plex Sans;
  font-size: 16px;
  color: #1e252a;
  outline: none;

  &:focus {
    border-color: ${props => props.$hasError ? '#DC2626' : '#7200E0'};
    box-shadow: 0 0 0 2px ${props => props.$hasError ? 'rgba(220, 38, 38, 0.1)' : 'rgba(114, 0, 224, 0.1)'};
  }

  &::placeholder {
    color: #9E9E9E;
  }
`;

const DeleteButton = styled.button`
  width: 36px;
  height: 36px;
  border: none;
  background-color: transparent;
  cursor: pointer;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.6;
  transition: all 0.2s;
  flex-shrink: 0;

  &:hover {
    background-color: #FEE2E2;
    opacity: 1;
  }

  img {
    width: 20px;
    height: 20px;
  }
`;

const AddCriteriaButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: 1px solid #7200E0;
  border-radius: 20px;
  background-color: transparent;
  cursor: pointer;
  font-family: IBM Plex Sans;
  font-size: 14px;
  font-weight: 500;
  color: #7200E0;
  transition: all 0.2s;
  margin-top: 8px;

  &:hover {
    background-color: #F5EDFC;
  }

  img {
    width: 16px;
    height: 16px;
  }
`;

const EmptyState = styled.div`
  padding: 24px;
  text-align: center;
  color: #7b7382;
  font-family: IBM Plex Sans;
  font-size: 14px;
  border: 1px dashed #C9C6CC;
  border-radius: 8px;
  margin-bottom: 16px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  min-width: 0;
  width: 100%;
`;

const BackArrow = styled.span`
  font-family: IBM Plex Sans;
  font-size: 20px;
  font-weight: 400;
  color: #7200E0;
  cursor: pointer;
  padding: 4px 8px;
  flex-shrink: 0;
  &:hover {
    opacity: 0.8;
  }
`;

const DetailsStrip = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 12px 24px;
  margin: 4px 0 24px 0;
  background: #F5F0FF;
  border-radius: 0;
  border: 1px solid #E8E0F5;
  box-sizing: border-box;
  overflow: hidden;
  isolation: isolate;
`;

const RangeRemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1px solid #C9C6CC;
  background-color: transparent;
  cursor: pointer;
  flex-shrink: 0;
  font-size: 14px;
  color: #7b7382;
  transition: all 0.2s;
  padding: 0;

  &:hover:not(:disabled) {
    background-color: #FEE2E2;
    border-color: #DC2626;
    color: #DC2626;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

// Stable key per criterion for local-state maps (localCriterias,
// expandedRangeRows, validationErrors). `_clientId` wins over the backend
// `id` because it's the FIRST identifier assigned (on addCriteria, before
// the row is ever saved), so any click handler captured during the row's
// lifetime sees the same rowKey — even as a first-save of that row
// round-trips and attaches a backend id. The hook preserves `_clientId`
// through save responses (mergeClientIds) to keep this guarantee. Rows
// loaded from the server without a `_clientId` fall through to `id:`.
// Plain index is the final fallback for truly unidentified drafts.
const criteriaKey = (criteriaItem, index) => {
  if (!criteriaItem) return `idx:${index}`;
  if (criteriaItem._clientId) return `cid:${criteriaItem._clientId}`;
  if (criteriaItem.id) return `id:${criteriaItem.id}`;
  return `idx:${index}`;
};

// Strict integer parse: returns NaN for anything that isn't a plain integer
// (e.g. "3.5", "3e1", "-", "abc"). parseInt() silently truncates those and
// would misgrade criteria / silently clear ranges on the backend.
const parseIntegerStrict = (raw) => {
  if (raw === null || raw === undefined) return NaN;
  const str = String(raw).trim();
  if (str === '') return NaN;
  if (!/^-?\d+$/.test(str)) return NaN;
  return parseInt(str, 10);
};

// Re-find a criterion in a fresh list using a pre-save snapshot. Click
// handlers captured before a blur-save hold a snapshot where the row may
// still be unsaved (no id); after the save the array may have been
// re-sorted and the row gained an id. Matching priority:
//   1. backend id (persisted rows)
//   2. client-side draft id (unsaved rows — disambiguates identical drafts)
//   3. content signature (handles the just-saved case where _clientId was
//      stripped by the backend round-trip)
//   4. original index (absolute last resort)
const findCriterionIndex = (criterias, snapshot, fallbackIndex) => {
  if (!Array.isArray(criterias) || !snapshot) return -1;
  if (snapshot.id) {
    const byId = criterias.findIndex((c) => c && c.id === snapshot.id);
    if (byId >= 0) return byId;
  }
  if (snapshot._clientId) {
    const byClientId = criterias.findIndex(
      (c) => c && c._clientId === snapshot._clientId
    );
    if (byClientId >= 0) return byClientId;
  }
  const byContent = criterias.findIndex(
    (c) =>
      c
      && (c.criteria ?? '') === (snapshot.criteria ?? '')
      && (c.marks ?? null) === (snapshot.marks ?? null)
      && (c.marksFrom ?? null) === (snapshot.marksFrom ?? null)
  );
  if (byContent >= 0) return byContent;
  if (
    fallbackIndex >= 0
    && fallbackIndex < criterias.length
    && criterias[fallbackIndex]
  ) {
    return fallbackIndex;
  }
  return -1;
};

const ShortAnswerDetail = () => {
  const { t } = useLexicon();
  const { shortAnswerId } = useParams();
  const history = useHistory();
  const { data: profile } = useProfile();
  const { states: userStates } = useUserStates();
  const userState = userStates[0] || null;

  const {
    shortAnswer,
    isLoading,
    isSaving,
    updateShortAnswerTitle,
    addCriteria,
    updateCriteria,
    deleteCriteria,
    clearAllMarkRanges,
    getCurrentShortAnswer,
  } = useShortAnswer(shortAnswerId);

  // Get label mappings for displaying current settings
  const { taskTypeLabels, responseTypeLabels } = useSettingsFilters({
    state: userState,
    initialYears: shortAnswer?.years || [],
    initialSubjects: (shortAnswer?.metadata?.subjects || []).map((s) =>
      typeof s === 'object' ? (s.subjectCode || s.code || s) : s
    ),
    initialTaskTypes: shortAnswer?.taskTypes || [],
    initialResponseTypes: shortAnswer?.responseTypes || [],
    enableYearFilter: false,
  });

  const [isShowMenu, setShowMenu] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [localCriterias, setLocalCriterias] = useState({});
  const [rangesEnabled, setRangesEnabled] = useState(false);
  const [expandedRangeRows, setExpandedRangeRows] = useState(new Set());
  const [validationErrors, setValidationErrors] = useState({});
  const [showSettingsPopup, setShowSettingsPopup] = useState(false);
  // Promise of the currently in-flight save (or null). Click handlers that
  // come right after a blur-save need to wait for that save to resolve — not
  // bail out (which would drop the first click and make teachers click
  // twice). `awaitInFlight` resolves as soon as the ref is clear, then the
  // handler proceeds with fresh state.
  //
  // The `.catch(() => {})` is load-bearing: if the previous save rejected (e.g.
  // network/API error), the follow-up handler's `await awaitInFlight()` would
  // otherwise inherit that rejection and abort before running its own work —
  // silently swallowing the teacher's first retry click. Each handler tracks
  // its own save errors in its own try/catch, so dropping the previous one
  // here is safe.
  const saveInFlightRef = useRef(null);
  const awaitInFlight = () =>
    saveInFlightRef.current
      ? saveInFlightRef.current.catch(() => {})
      : Promise.resolve();
  const tabletView = isTabletView();

  // Key derived from persisted range indexes so an effect can react when a
  // criterion's marksFrom appears/disappears server-side (not only on
  // short-answer id change). Keeps the toggle state honest when the last
  // marksFrom is cleared manually.
  const persistedRangeKey = (shortAnswer?.criterias ?? [])
    .map((c) => (c.marksFrom !== undefined && c.marksFrom !== null ? '1' : '0'))
    .join('');

  // On short-answer change: fully sync toggle + expansion to data. Runs when
  // a teacher switches between short answers.
  useEffect(() => {
    if (!shortAnswer?.criterias) return;
    const hasAnyRange = shortAnswer.criterias.some(
      (c) => c.marksFrom !== undefined && c.marksFrom !== null
    );
    setRangesEnabled(hasAnyRange);
    setExpandedRangeRows(new Set());
  }, [shortAnswer?.id]);

  // Within the same short answer: auto-enable toggle when persisted ranges
  // appear, but never auto-disable (the teacher's explicit "on" intent must
  // survive adding a blank criterion — which would otherwise flip the toggle
  // off because no marksFrom is set yet). Expansion keys are id-based and
  // pruned to rows that still exist.
  useEffect(() => {
    if (!shortAnswer?.criterias) return;
    const hasAnyRange = shortAnswer.criterias.some(
      (c) => c.marksFrom !== undefined && c.marksFrom !== null
    );
    if (hasAnyRange) setRangesEnabled(true);
    const currentKeys = new Set(
      shortAnswer.criterias.map((c, i) => criteriaKey(c, i))
    );
    setExpandedRangeRows((prev) => {
      const next = new Set();
      for (const rowKey of prev) {
        if (currentKeys.has(rowKey)) next.add(rowKey);
      }
      return next;
    });
  }, [persistedRangeKey]);

  const clearValidationError = useCallback((rowKey, field) => {
    setValidationErrors(prev => {
      const errorKey = `${rowKey}_${field}`;
      if (!prev[errorKey]) return prev;
      const next = { ...prev };
      delete next[errorKey];
      return next;
    });
  }, []);

  const handleTitleClick = () => {
    if (!canEdit) return;
    setIsEditingTitle(true);
    setEditTitle(shortAnswer.title);
  };

  const handleTitleChange = (event) => {
    setEditTitle(event.target.value);
  };

  const handleTitleKeyPress = (event) => {
    if (event.key === 'Enter') {
      saveTitle();
    }
  };

  const handleTitleBlur = () => {
    saveTitle();
  };

  const saveTitle = async () => {
    if (isEditingTitle) {
      if (!editTitle.trim() || editTitle === shortAnswer.title) {
        setIsEditingTitle(false);
        return;
      }

      try {
        await updateShortAnswerTitle({ title: editTitle.trim() });
      } catch (error) {
        console.error('Error updating short answer title:', error);
      }

      setIsEditingTitle(false);
    }
  };

  const handleMarksChange = (index, value) => {
    const criteriaItem = shortAnswer?.criterias?.[index];
    const rowKey = criteriaKey(criteriaItem, index);
    clearValidationError(rowKey, 'marks');
    setLocalCriterias(prev => ({
      ...prev,
      [rowKey]: { ...prev[rowKey], marks: value }
    }));
  };

  const handleMarksFromChange = (index, value) => {
    const criteriaItem = shortAnswer?.criterias?.[index];
    const rowKey = criteriaKey(criteriaItem, index);
    clearValidationError(rowKey, 'marksFrom');
    setLocalCriterias(prev => ({
      ...prev,
      [rowKey]: { ...prev[rowKey], marksFrom: value }
    }));
  };

  const handleCriteriaTextChange = (index, value) => {
    const criteriaItem = shortAnswer?.criterias?.[index];
    const rowKey = criteriaKey(criteriaItem, index);
    clearValidationError(rowKey, 'criteria');
    setLocalCriterias(prev => ({
      ...prev,
      [rowKey]: { ...prev[rowKey], criteria: value }
    }));
  };

  const handleCriteriaBlur = async (index) => {
    // Snapshot the row the blur was for BEFORE awaiting — the array may be
    // reordered by the save we're waiting on, so we can't rely on `index`
    // resolving to the same row afterwards.
    const snapshot = shortAnswer?.criterias?.[index];
    // Serialise against any in-flight save. If one is running (e.g. the user
    // quickly moves focus between rows), wait for it to land so the PATCH we
    // build here uses the freshest shortAnswer state.
    await awaitInFlight();
    const current = getCurrentShortAnswer();
    const resolvedIndex = findCriterionIndex(current?.criterias, snapshot, index);
    if (resolvedIndex < 0) return;
    const criteriaItem = current.criterias[resolvedIndex];
    const rowKey = criteriaKey(criteriaItem, resolvedIndex);
    const localData = localCriterias[rowKey];
    if (!localData) return;

    const marks = localData.marks !== undefined
      ? parseIntegerStrict(localData.marks)
      : criteriaItem.marks;
    const criteria = localData.criteria !== undefined ? localData.criteria : criteriaItem.criteria;
    const rawMarksFrom = localData.marksFrom !== undefined ? localData.marksFrom : criteriaItem.marksFrom;
    const marksFrom = rawMarksFrom !== undefined && rawMarksFrom !== null && rawMarksFrom !== ''
      ? parseIntegerStrict(rawMarksFrom)
      : null;

    const hasCriteriaText = criteria && criteria.trim() !== '';
    // marks = 0 is a legitimate criterion (e.g. "no attempt" scoring bucket);
    // only NaN or negatives are real errors.
    const hasMarksValue = !isNaN(marks) && marks >= 0;
    const hasChanges = marks !== criteriaItem.marks
      || criteria !== criteriaItem.criteria
      || marksFrom !== (criteriaItem.marksFrom ?? null);

    const newErrors = {};
    if (!hasCriteriaText && (hasMarksValue || criteriaItem.id)) {
      newErrors[`${rowKey}_criteria`] = true;
    }
    if (!hasMarksValue && (hasCriteriaText || criteriaItem.id)) {
      newErrors[`${rowKey}_marks`] = true;
    }
    // Any non-null marksFrom must be a valid integer, non-negative, strictly
    // less than marks. isNaN covers decimal/exponent/garbage input.
    if (marksFrom !== null) {
      if (isNaN(marksFrom)
          || marksFrom < 0
          || (!isNaN(marks) && marksFrom >= marks)) {
        newErrors[`${rowKey}_marksFrom`] = true;
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setValidationErrors(prev => ({ ...prev, ...newErrors }));
      return;
    }

    if (hasChanges && hasCriteriaText && hasMarksValue) {
      // Hold the save as a promise so any click handler that fires next can
      // awaitInFlight() and run against post-save state instead of being
      // dropped. Pass the stable rowKey so the hook resolves against the
      // latest criterias array (critical when a prior save re-sorted rows).
      const savePromise = updateCriteria(rowKey, { marks, criteria, marksFrom });
      saveInFlightRef.current = savePromise;
      try {
        await savePromise;
        setLocalCriterias(prev => {
          const newState = { ...prev };
          delete newState[rowKey];
          return newState;
        });
        // Clear any validation flags set on this row in a prior blur — e.g.
        // marksFrom >= marks. Once a row saves cleanly, its inputs should no
        // longer be shown in an error state.
        setValidationErrors(prev => {
          const next = {};
          for (const errorKey in prev) {
            if (!errorKey.startsWith(`${rowKey}_`)) next[errorKey] = prev[errorKey];
          }
          return next;
        });
      } catch (error) {
        console.error('Error updating criteria:', error);
      } finally {
        if (saveInFlightRef.current === savePromise) saveInFlightRef.current = null;
      }
    } else if (!hasChanges) {
      setLocalCriterias(prev => {
        const newState = { ...prev };
        delete newState[rowKey];
        return newState;
      });
    }
    // Incomplete rows stay in localCriterias so unsaved marks/ranges survive the blur.
  };

  const handleDeleteCriteria = async (index) => {
    // Capture the target row BEFORE awaiting so we can re-find it in fresh
    // state even if a blur-save re-sorts or adds an id to it.
    const snapshot = shortAnswer?.criterias?.[index];
    // Serialise: if a blur-save for this or another row is in flight, wait
    // for it to land before issuing the delete. Returning early would drop
    // the teacher's click (blur fires before click, so the first click after
    // an edit would otherwise be a no-op).
    await awaitInFlight();
    const current = getCurrentShortAnswer();
    const resolvedIndex = findCriterionIndex(current?.criterias, snapshot, index);
    if (resolvedIndex < 0) return;
    const criteriaItem = current.criterias[resolvedIndex];
    const rowKey = criteriaKey(criteriaItem, resolvedIndex);
    setValidationErrors(prev => {
      const next = { ...prev };
      delete next[`${rowKey}_marks`];
      delete next[`${rowKey}_marksFrom`];
      delete next[`${rowKey}_criteria`];
      return next;
    });
    setLocalCriterias(prev => {
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
    setExpandedRangeRows(prev => {
      const next = new Set(prev);
      next.delete(rowKey);
      return next;
    });

    const deletePromise = deleteCriteria(rowKey);
    saveInFlightRef.current = deletePromise;
    try {
      await deletePromise;
    } catch (error) {
      console.error('Error deleting criteria:', error);
    } finally {
      if (saveInFlightRef.current === deletePromise) saveInFlightRef.current = null;
    }
  };

  const handleAddCriteria = async () => {
    // Wait for any in-flight blur-save/clear/delete to resolve first — an
    // optimistic add against pre-save state would get clobbered by the
    // in-flight PATCH's response setting shortAnswer back.
    await awaitInFlight();
    const newCriteria = {
      marks: 0,
      criteria: ''
    };

    try {
      await addCriteria(newCriteria);
    } catch (error) {
      console.error('Error adding criteria:', error);
    }
  };

  // Strip every marksFrom draft/error/expansion without touching the other
  // in-flight fields (marks, criteria). Used when the teacher turns mark
  // ranges off — otherwise a stale marksFrom draft would silently get
  // persisted on the next blur.
  const scrubMarksFromDrafts = () => {
    setLocalCriterias(prev => {
      const next = {};
      for (const rowKey in prev) {
        const { marksFrom, ...rest } = prev[rowKey];
        if (Object.keys(rest).length > 0) next[rowKey] = rest;
      }
      return next;
    });
    setValidationErrors(prev => {
      const next = {};
      for (const errorKey in prev) {
        if (!errorKey.endsWith('_marksFrom')) next[errorKey] = prev[errorKey];
      }
      return next;
    });
    setExpandedRangeRows(new Set());
  };

  const toggleRangesEnabled = async () => {
    // Serialise against any in-flight row save so the clear-all PATCH runs on
    // fresh state. Waiting (not bailing) is important because clicking the
    // toggle right after a blur is a common flow.
    await awaitInFlight();
    if (rangesEnabled) {
      // Read fresh state via the hook accessor: after an awaited save, the
      // component's closed-over `shortAnswer` is still the pre-save value,
      // so new ranges from that save would be missed here.
      const current = getCurrentShortAnswer();
      const hasPersistedRanges = current?.criterias?.some(
        c => c.marksFrom !== undefined && c.marksFrom !== null
      );
      if (hasPersistedRanges) {
        const confirmed = window.confirm(
          'Turning off mark ranges will remove them from all criteria. Continue?'
        );
        if (!confirmed) return;
        const clearPromise = clearAllMarkRanges();
        saveInFlightRef.current = clearPromise;
        try {
          await clearPromise;
        } catch (err) {
          console.error('Error clearing mark ranges:', err);
          return;
        } finally {
          if (saveInFlightRef.current === clearPromise) saveInFlightRef.current = null;
        }
      }
      setRangesEnabled(false);
      scrubMarksFromDrafts();
    } else {
      setRangesEnabled(true);
    }
  };

  const toggleRowRange = async (rowKey, originalIndex, snapshot) => {
    // Serialise against any in-flight blur-save: when the teacher types in a
    // brand-new criterion, tabs out (blur → save) and immediately clicks +,
    // the save re-keys the row from `cid:<x>` to `id:<y>`. If we keyed the
    // expansion state under the stale rowKey, the post-save render's
    // showRangeInput lookup would miss and the range input would never appear.
    await awaitInFlight();
    const current = getCurrentShortAnswer();
    // Re-resolve against fresh criterias. Fall back to the original rowKey if
    // we can't find the row (e.g. unsaved-draft case where snapshot/index
    // weren't provided by an older caller).
    const resolvedIndex = snapshot
      ? findCriterionIndex(current?.criterias, snapshot, originalIndex)
      : -1;
    const criteriaItem = resolvedIndex >= 0 ? current.criterias[resolvedIndex] : null;
    const resolvedRowKey = criteriaItem
      ? criteriaKey(criteriaItem, resolvedIndex)
      : rowKey;

    setExpandedRangeRows(prev => {
      const next = new Set(prev);
      // Clean up any stale expansion entry under the pre-save rowKey too, so
      // switching rowKeys mid-flight doesn't leave orphan state behind.
      if (rowKey !== resolvedRowKey) next.delete(rowKey);
      if (next.has(resolvedRowKey)) {
        next.delete(resolvedRowKey);
        setLocalCriterias(prevLocal => ({
          ...prevLocal,
          [resolvedRowKey]: { ...prevLocal[resolvedRowKey], marksFrom: '' }
        }));
        clearValidationError(resolvedRowKey, 'marksFrom');
      } else {
        next.add(resolvedRowKey);
      }
      return next;
    });
  };

  // Per-row "remove range" action. Persists marksFrom=null for the clicked
  // criterion without touching the global toggle or other rows. Covers the
  // case where a teacher added a range in error and wants to revert only
  // that single row back to a single mark.
  const removeRowRange = async (rowKey, originalIndex, snapshot) => {
    // Serialise against any in-flight save so we read the post-save state
    // when resolving the target criterion.
    await awaitInFlight();
    // Re-resolve the target row against the freshest state. The snapshot
    // captured at click time lets us find the same criterion even after a
    // save re-sorts the list or assigns an id to a previously-unsaved row.
    const current = getCurrentShortAnswer();
    const resolvedIndex = findCriterionIndex(current?.criterias, snapshot, originalIndex);
    if (resolvedIndex < 0) return;
    const criteriaItem = current.criterias[resolvedIndex];
    const resolvedRowKey = criteriaKey(criteriaItem, resolvedIndex);

    // Clean up local state under BOTH the original rowKey (from render) and
    // the resolved one — they can differ if a save assigned an id mid-flight.
    const keysToClean = rowKey === resolvedRowKey ? [rowKey] : [rowKey, resolvedRowKey];
    setExpandedRangeRows(prev => {
      const next = new Set(prev);
      keysToClean.forEach((k) => next.delete(k));
      return next;
    });
    setLocalCriterias(prev => {
      const next = { ...prev };
      keysToClean.forEach((k) => {
        const { marksFrom: _dropped, ...rest } = next[k] || {};
        if (Object.keys(rest).length > 0) {
          next[k] = rest;
        } else {
          delete next[k];
        }
      });
      return next;
    });
    keysToClean.forEach((k) => clearValidationError(k, 'marksFrom'));

    // Only hit the backend if the range is actually persisted (on the fresh
    // row, not the pre-await snapshot); otherwise the user was just
    // cancelling an in-flight expansion. Send only the marksFrom field —
    // the hook spreads this over current criterion state so we won't
    // overwrite freshly-saved marks or criteria text.
    if (criteriaItem.marksFrom !== undefined && criteriaItem.marksFrom !== null) {
      const removePromise = updateCriteria(resolvedRowKey, { marksFrom: null });
      saveInFlightRef.current = removePromise;
      try {
        await removePromise;
      } catch (err) {
        console.error('Error removing mark range:', err);
      } finally {
        if (saveInFlightRef.current === removePromise) saveInFlightRef.current = null;
      }
    }
  };

  const handleSettingsSave = async (settings) => {
    try {
      await updateShortAnswerTitle({
        years: settings.years || [],
        subjects: settings.subjects || [],
        taskTypes: settings.taskTypes || (settings.taskType ? [settings.taskType] : []),
        responseTypes: settings.responseTypes || (settings.responseType ? [settings.responseType] : []),
      });
      toast(<Toast message="Settings saved successfully" />);
    } catch (error) {
      console.error('Error updating settings:', error);
      toast(<Toast message="Failed to save settings" />);
    }
  };

  if (isLoading) {
    return <Loader />;
  }

  if (!shortAnswer) {
    return (
      <div>
        Short answer not found
      </div>
    );
  }

  const canEdit = !shortAnswer.isSystem;

  return (
    <MainContainer>
      <ImprovedSecondarySideBar
        isShowMenu={isShowMenu}
        setShowMenu={setShowMenu}
        id={shortAnswerId}
      />
      <InnerContainer>
        {tabletView && (
          <HeadingAndFilterContainer>
            <MenuButton setShowMenu={setShowMenu} />
          </HeadingAndFilterContainer>
        )}

        <RightContainer>
          <ContentContainer>
            <TitleSection>
            <TitleRow>
              <BackArrow onClick={() => history.push('/tools/shortanswers')}>&lt;</BackArrow>
              <TitleContainer onClick={handleTitleClick} canEdit={canEdit}>
                {isEditingTitle ? (
                  <TitleInput
                    value={editTitle}
                    onChange={handleTitleChange}
                    onBlur={handleTitleBlur}
                    onKeyPress={handleTitleKeyPress}
                    autoFocus
                  />
                ) : (
                  <>
                    <Title>{shortAnswer.title}</Title>
                    {canEdit && (
                      <EditIconContainer>
                        <EditIconStyled src={editIcon} alt="Edit title" />
                        <EditIconHover src={EditHover} alt="Edit title" />
                      </EditIconContainer>
                    )}
                  </>
                )}
              </TitleContainer>
            </TitleRow>
            </TitleSection>
            <DetailsStrip>
              <SettingsTagsDisplay
                years={shortAnswer.years || (shortAnswer.year ? [shortAnswer.year] : [])}
                subjects={shortAnswer.metadata?.subjects?.length ? shortAnswer.metadata.subjects : (shortAnswer.subjects || [])}
                taskTypes={shortAnswer.taskTypes || (shortAnswer.taskType ? [shortAnswer.taskType] : [])}
                responseTypes={shortAnswer.responseTypes || (shortAnswer.responseType ? [shortAnswer.responseType] : [])}
                taskTypeLabels={taskTypeLabels}
                responseTypeLabels={responseTypeLabels}
                onSettingsClick={canEdit ? () => setShowSettingsPopup(true) : undefined}
                showSettingsButton={canEdit}
              />
            </DetailsStrip>

            <MainContentSection>
            <HighestMarksContainer>
              <HighestMarksLabel>{t('Highest Marks')}</HighestMarksLabel>
              <HighestMarksValue>
                {Math.max(0, ...(shortAnswer.criterias?.map(c => c.marks ?? 0) || []))}
              </HighestMarksValue>
            </HighestMarksContainer>

            {canEdit && (
              <ToggleContainer>
                {/*
                  Intentionally not `disabled` during isSaving: browsers
                  swallow click events on disabled controls, which would drop
                  the teacher's first click after a blur-save. The handler
                  uses awaitInFlight() to serialise instead.
                */}
                <ToggleSwitch
                  $active={rangesEnabled}
                  onClick={toggleRangesEnabled}
                  type="button"
                >
                  <ToggleKnob $active={rangesEnabled} />
                </ToggleSwitch>
                <ToggleLabel>{t('Use mark ranges')}</ToggleLabel>
              </ToggleContainer>
            )}

            <CriteriaHeader>
              <MarksHeaderLabel>{t('Marks')}</MarksHeaderLabel>
              <CriteriaHeaderLabel>Criteria</CriteriaHeaderLabel>
            </CriteriaHeader>

            <CriteriaList>
              {shortAnswer.criterias && shortAnswer.criterias.length > 0 ? (
                shortAnswer.criterias.map((criteriaItem, index) => {
                  const rowKey = criteriaKey(criteriaItem, index);
                  const localData = localCriterias[rowKey] || {};
                  const marksValue = localData.marks !== undefined ? localData.marks : criteriaItem.marks;
                  const criteriaValue = localData.criteria !== undefined ? localData.criteria : criteriaItem.criteria;

                  const hasMarksFromValue = criteriaItem.marksFrom !== undefined && criteriaItem.marksFrom !== null;
                  const isRangeExpanded = rangesEnabled && expandedRangeRows.has(rowKey);
                  const showRangeInput = isRangeExpanded || hasMarksFromValue;
                  const marksFromValue = localData.marksFrom !== undefined
                    ? localData.marksFrom
                    : (criteriaItem.marksFrom ?? '');

                  const marksError = !!validationErrors[`${rowKey}_marks`];
                  const marksFromError = !!validationErrors[`${rowKey}_marksFrom`];
                  const criteriaError = !!validationErrors[`${rowKey}_criteria`];

                  return (
                    <CriteriaItem key={rowKey}>
                      <MarksInputGroup>
                        {showRangeInput && (
                          <>
                            <MarksInput
                              type="number"
                              min="0"
                              value={marksFromValue}
                              onChange={(e) => handleMarksFromChange(index, e.target.value)}
                              onBlur={() => handleCriteriaBlur(index)}
                              disabled={!canEdit}
                              placeholder="0"
                              title="Range start"
                              $hasError={marksFromError}
                            />
                            <MarksSeparator>–</MarksSeparator>
                          </>
                        )}
                        <MarksInput
                          type="number"
                          min="0"
                          value={marksValue}
                          onChange={(e) => handleMarksChange(index, e.target.value)}
                          onBlur={() => handleCriteriaBlur(index)}
                          disabled={!canEdit}
                          $hasError={marksError}
                        />
                        {rangesEnabled && !showRangeInput && canEdit && (
                          <RangeExpandButton
                            onClick={() => toggleRowRange(rowKey, index, criteriaItem)}
                            type="button"
                            title={t('Add mark range')}
                          >
                            +
                          </RangeExpandButton>
                        )}
                        {showRangeInput && canEdit && (
                          // Intentionally not `disabled` during isSaving: the handler
                          // serialises via awaitInFlight(), and browsers swallow click
                          // events on disabled controls which would drop the teacher's
                          // first click after a blur-save races the click.
                          <RangeRemoveButton
                            onClick={() => removeRowRange(rowKey, index, criteriaItem)}
                            type="button"
                            title={t('Remove mark range')}
                          >
                            ×
                          </RangeRemoveButton>
                        )}
                      </MarksInputGroup>
                      <CriteriaInput
                        type="text"
                        placeholder="Enter criteria..."
                        value={criteriaValue}
                        onChange={(e) => handleCriteriaTextChange(index, e.target.value)}
                        onBlur={() => handleCriteriaBlur(index)}
                        disabled={!canEdit}
                        $hasError={criteriaError}
                      />
                      {canEdit && (
                        <DeleteButton
                          onClick={() => handleDeleteCriteria(index)}
                          aria-label="Delete criteria"
                        >
                          <img src={deleteIcon} alt="Delete" />
                        </DeleteButton>
                      )}
                    </CriteriaItem>
                  );
                })
              ) : (
                <EmptyState>
                  No criteria added yet. Click "Add Criteria" to get started.
                </EmptyState>
              )}
            </CriteriaList>

            {canEdit && (
              <AddCriteriaButton onClick={handleAddCriteria}>
                <img src={plusIcon} alt="Add" />
                Add Criteria
              </AddCriteriaButton>
            )}
            </MainContentSection>
          </ContentContainer>
        </RightContainer>
      </InnerContainer>

      <ItemSettingsModal
        open={showSettingsPopup}
        onClose={() => setShowSettingsPopup(false)}
        item={shortAnswer}
        state={userState}
        onSave={handleSettingsSave}
      />
    </MainContainer>
  );
};

export default ShortAnswerDetail;
